import type { ThresholdSport } from '@painpal/contracts';
import { useCallback, useState } from 'react';
import type { SessionUser } from '../../auth/session';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { AccountCard } from './AccountCard';
import { DangerZone } from './DangerZone';
import { FtpHistoryCard } from './FtpHistoryCard';
import { HrZonesCard } from './HrZonesCard';
import { OtherThresholds } from './OtherThresholds';
import { RecalcBanner, useRecalculation, type Recalculation } from './RecalcBanner';
import { RunThresholds } from './RunThresholds';
import { SetPasswordCard } from './SetPasswordCard';
import { SportSwitcher } from './SportSwitcher';
import { StravaCard } from './StravaCard';
import styles from './settings.module.css';

interface ViewProps {
  sport: ThresholdSport; today: string; recalculation: Recalculation; focusHrZones: boolean; onRide: () => void; onHrZonesFocused: () => void;
}

function SportView({ sport, today, recalculation, focusHrZones, onRide, onHrZonesFocused }: ViewProps) {
  if (sport === 'ride') {
    return (
      <>
        <div className={styles.thresholds}>
          <RecalcBanner {...recalculation} slot="ftp" />
          <FtpHistoryCard today={today} />
        </div>
        <div className={styles.sideTop}>
          <RecalcBanner {...recalculation} slot="hr" />
          <HrZonesCard today={today} focusHeading={focusHrZones} onFocused={onHrZonesFocused} />
        </div>
      </>
    );
  }
  const View = sport === 'run' ? RunThresholds : OtherThresholds;
  return (
    <>
      {/* One banner for both columns here, so it words every recalculation, a ride HR one included. */}
      <RecalcBanner {...recalculation} slot={recalculation.kind} />
      <View today={today} onEditMaxHr={onRide} />
    </>
  );
}

export interface SettingsPageProps {
  user: SessionUser;
  today: string;
  stravaError?: string;
  sport: ThresholdSport;
  onSportChange: (sport: ThresholdSport) => void;
}

export function SettingsPage({ user, today, stravaError, sport, onSportChange }: SettingsPageProps) {
  const recalculation = useRecalculation();
  // EDIT UNDER RIDE unmounts the button it was pressed on, so focus moves to the HR ZONES card it sends you to. Cleared
  // once it has, so back and forward remounting the card don't take focus again.
  const [focusHrZones, setFocusHrZones] = useState(false);
  const clearFocusHrZones = useCallback(() => setFocusHrZones(false), []);
  const switchTo = (next: ThresholdSport, focusHr: boolean): void => { setFocusHrZones(focusHr); onSportChange(next); };
  return (
    <div className={`page ${styles.page}`}>
      <header className="page-heading">
        <Eyebrow>SETTINGS · THRESHOLDS PER SPORT, CONNECTIONS, ACCOUNT</Eyebrow>
        <Headline first="YOUR" second="NUMBERS." />
      </header>
      <SportSwitcher sport={sport} onChange={(next) => switchTo(next, false)} />
      <div className={styles.main}>
        <div className={styles.sportArea}>
          <SportView sport={sport} today={today} recalculation={recalculation} focusHrZones={focusHrZones} onRide={() => switchTo('ride', true)}
            onHrZonesFocused={clearFocusHrZones} />
        </div>
        <div className={styles.side}>
          <StravaCard returnError={stravaError} />
          <SetPasswordCard />
          <AccountCard user={user} />
          <DangerZone />
        </div>
      </div>
    </div>
  );
}
